import { lastValueFrom } from 'rxjs';

import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiService, HTTP_TIMEOUT } from '@movilidad/app-core';

import { BookingRoute, CartItem, PackageCoverage, PaymentMethod, PurchasedTicket, StopDepartures, TripDirection } from './booking.model';

/** Igual que en Movilidad: una compra puede tardar más que el límite normal, y no se reintenta. */
const PURCHASE_TIMEOUT_MS = 100_000;

export const bookingKeys = {
  all: ['booking'] as const,
  routes: (direction: TripDirection) => ['booking', 'routes', direction] as const,
  recents: (userId: number | string | undefined, direction: TripDirection) => ['booking', 'recents', userId, direction] as const,
  departures: (stopId: number, date: string) => ['booking', 'departures', stopId, date] as const,
  balance: ['booking', 'balance'] as const,
  coverage: (item: Pick<CartItem, 'realtripId' | 'stopId'>) => ['booking', 'coverage', item.realtripId, item.stopId] as const,
};

/** El API rechazó la compra y no se cobró nada. `itemErrors` va por índice de boleto del carrito. */
export class PurchaseRejected extends Error {
  constructor(
    message: string,
    readonly itemErrors: Record<number, string> = {}
  ) {
    super(message);
  }
}

/** Se cortó la red o venció el tiempo: la compra pudo haberse hecho. No hay que ofrecer reintentar a ciegas. */
export class PurchaseUnconfirmed extends Error {}

interface Rejection {
  success?: boolean;
  error?: string;
  message?: string;
  errors?: unknown;
}

/** El API también rechaza con 200 y `success: false`. Sus mensajes llegan tal cual al usuario. */
function rejectionMessage(body: Rejection | null | undefined): string {
  return body?.error || body?.message || 'No se pudo completar la compra. No se te cobró nada.';
}

function purchaseError(error: unknown): Error {
  if (!(error instanceof HttpErrorResponse)) return error instanceof Error ? error : new Error(String(error));
  if (error.status === 0 || error.status >= 500) {
    return new PurchaseUnconfirmed('No pudimos confirmar tu compra. Revisa tus boletos antes de intentar de nuevo.');
  }

  const body = error.error as Rejection | null;
  const itemErrors: Record<number, string> = {};
  // Una validación de Laravel también es 422, pero su `errors` es un objeto y no una lista.
  if (Array.isArray(body?.errors)) {
    for (const item of body.errors as { index: number; error: string }[]) itemErrors[item.index] = item.error;
  }
  return new PurchaseRejected(rejectionMessage(body), itemErrors);
}

@Injectable({ providedIn: 'root' })
export class BookingApi {
  private readonly api = inject(ApiService);

  async routes(direction: TripDirection): Promise<BookingRoute[]> {
    const response = await lastValueFrom(
      this.api.get<{ data?: BookingRoute[] } & Rejection>(`routes?type=${direction}&show_stops=1`)
    );
    if (response.success === false) throw new Error(response.error ?? 'No pudimos cargar las paradas.');
    return response.data ?? [];
  }

  async departures(stopId: number, date: string): Promise<StopDepartures> {
    const response = await lastValueFrom(this.api.get<{ data: StopDepartures }>(`trips/${stopId}?date=${date}`));
    return response.data;
  }

  async balance(): Promise<number> {
    const response = await lastValueFrom(this.api.get<{ balance: number | string }>('balance'));
    return Number(response.balance) || 0;
  }

  /**
   * Si un paquete cubre este viaje. Durante un periodo de registro el API responde sin `data`, y un error
   * tampoco debe impedir pagar con otro método: en ambos casos, no hay paquete que ofrecer.
   */
  async packageCoverage(item: CartItem): Promise<PackageCoverage> {
    const response = await lastValueFrom(
      this.api.get<{ data?: { package?: { enabled?: boolean; quantity?: number | null } } }>(
        `suscriptions/swap?type=${item.direction}&date=${item.date}&route_id=${item.routeId}&radial_id=${item.radialId ?? ''}`
      )
    ).catch(() => null);
    const coverage = response?.data?.package;
    return { enabled: !!coverage?.enabled, quantity: coverage?.quantity ?? null };
  }

  /** Un boleto: pasa por `bustickets`, que acepta todos los métodos. */
  async buyOne(item: CartItem, method: PaymentMethod): Promise<PurchasedTicket> {
    const response = await this.purchase<{ success: boolean; busticket: PurchasedTicket } & Rejection>('bustickets', {
      date: item.date,
      method,
      stop_id: item.stopId,
      realtrip_id: item.realtripId,
      amount: item.amount,
    });
    if (!response.success) throw new PurchaseRejected(rejectionMessage(response));
    return response.busticket;
  }

  /** Varios boletos, un solo cargo. El API sólo lo acepta con Wallet, y es todo o nada. */
  async buyMany(items: CartItem[]): Promise<PurchasedTicket[]> {
    const response = await this.purchase<{ success: boolean; bustickets: PurchasedTicket[] } & Rejection>('bustickets/batch', {
      method: 'wallet',
      items: items.map((item) => ({ realtrip_id: item.realtripId, stop_id: item.stopId, amount: item.amount })),
    });
    if (!response.success) throw new PurchaseRejected(rejectionMessage(response));
    return response.bustickets;
  }

  private async purchase<T>(url: string, body: object): Promise<T> {
    const context = new HttpContext().set(HTTP_TIMEOUT, PURCHASE_TIMEOUT_MS);
    try {
      return await lastValueFrom(this.api.post<object>(url, body, { context }));
    } catch (error) {
      throw purchaseError(error);
    }
  }
}
